import React, { useEffect, useState } from "react";
import CommonTable from "../../components/commonComponent/CommanTable";
import CommanFilters from "../../components/commonComponent/CommanFilters";
import axios from "../../utils/axiosInstance";
import {
  FaPlus,
  FaPencilAlt,
  FaTrashAlt,
  FaEye,
  FaFilter,
  FaEllipsisV
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toastSuccess, toastError, confirmDelete } from "../../utils/alertHelper";
import { hasPermission } from "../../utils/permissionHelper";

/* ================= COLUMNS ================= */
const columns = [
  { key: "srNo", label: "Sr No", width: 60 },
  { key: "Status", label: ""},
  { key: "name", label: "Client Name" },
  { key: "mobile", label: "Mobile Number" },
  { key: "email", label: "Email ID" },
  { key: "company", label: "Company Name" },
  { key: "gst", label: "GST No" },
  { key: "status", label: "Status" },
  { key: "category", label: "User Category" },
  { key: "accountStatus", label: "Account Status" },
  { key: "action", label: "Action", type: "actions", width: 140 }
];

const ClientIndex = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // search UI is present but handled client-side only
  const [openActionId, setOpenActionId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    userStatus: "",
    accountStatus: "",
    state: "",
    city: "",
    country: "",
    userCategory: ""
  });

  const clearFiltersDefault = {
    startDate: "",
    endDate: "",
    userStatus: "",
    accountStatus: "",
    state: "",
    city: "",
    country: "",
    userCategory: ""
  };

  const handleClearFilters = () => {
    setFilters(clearFiltersDefault);
    fetchClients();
  };

  const [filterDropdowns, setFilterDropdowns] = useState({ states: [], cities: [], countries: [], userCategories: [] });

  const getOptionVal = (it) => (it && (it._id ?? it.id ?? it.value)) || '';
  const getOptionLabel = (it) => {
    if (!it) return '';
    if (it.firstName) return `${it.firstName} ${it.lastName || ''}`.trim();
    return it.name || it.stateName || it.label || it.title || '';
  };
  const buildOptions = (list) => (Array.isArray(list) ? list.map(it => ({ value: getOptionVal(it), label: getOptionLabel(it) })) : []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [countriesRes, statesRes, citiesRes, catsRes] = await Promise.all([
          axios.get('/admin/api/country'),
          axios.get('/admin/api/state'),
          axios.get('/admin/api/city'),
          axios.get('/admin/api/userCategory'),
        ]);

        const extractArray = (res) => {
          const d = res?.data ?? res;
          if (!d) return [];
          if (Array.isArray(d)) return d;
          if (Array.isArray(d.data)) return d.data;
          if (Array.isArray(d.countries)) return d.countries;
          if (Array.isArray(d.states)) return d.states;
          if (Array.isArray(d.cities)) return d.cities;
          if (Array.isArray(d.userCategories)) return d.userCategories;
          const firstArray = Object.values(d).find(v => Array.isArray(v));
          if (Array.isArray(firstArray)) return firstArray;
          return [];
        };

        if (!mounted) return;
        setFilterDropdowns({
          countries: extractArray(countriesRes),
          states: extractArray(statesRes),
          cities: extractArray(citiesRes),
          userCategories: extractArray(catsRes),
        });
      } catch (err) {
        // silent - leave dropdowns empty
      }
    })();
    return () => { mounted = false; };
  }, []);

  /* =============== USER LOAD =============== */
  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
  }, []);

  /* =============== ROLE CHECK =============== */
  const isClientUser = user?.userType === "Client";

  /* =============== PERMISSIONS (UNCHANGED) =============== */
  const canViewClient = hasPermission(user, "Client registration", "View");
  const canCreateClient = hasPermission(user, "Client registration", "Create");
  const canEditClient = hasPermission(user, "Client registration", "Edit");
  const canDeleteClient = hasPermission(user, "Client registration", "Delete");

  /* =============== FETCH DATA =============== */
  const fetchClients = async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams();

      // 🔐 client → only own data
      if (isClientUser) {
        qp.append("clientId", user?._id);
      } else {
        qp.append("userType", "Client");
      }

      if (!isClientUser && searchQuery.length >= 3) {
        qp.append("search", searchQuery);
      }

      // apply only allowed filters
      if (filters.startDate) qp.append("startDate", filters.startDate);
      if (filters.endDate) qp.append("endDate", filters.endDate);
      if (filters.userStatus) qp.append("userStatus", filters.userStatus);
      else if (filters.status) qp.append("userStatus", filters.status);
      if (filters.accountStatus) qp.append("accountStatus", filters.accountStatus);
      if (filters.state) qp.append("state", filters.state);
      if (filters.city) qp.append("city", filters.city);
      if (filters.country) qp.append("country", filters.country);
      if (filters.userCategory) qp.append("userCategory", filters.userCategory);
      else if (filters.category) qp.append("userCategory", filters.category);

      const res = await axios.get(`/api/user?${qp.toString()}`);
      const extractUsers = (r) => {
        const d = r?.data ?? r;
        if (!d) return [];
        if (Array.isArray(d.users)) return d.users;
        if (Array.isArray(d.data?.users)) return d.data.users;
        if (Array.isArray(d.data)) return d.data;
        if (Array.isArray(d.usersList)) return d.usersList;
        if (Array.isArray(d)) return d;
        // try nested search
        if (typeof d === 'object') {
          const firstArray = Object.values(d).find(v => Array.isArray(v));
          if (Array.isArray(firstArray)) return firstArray;
        }
        return [];
      };
      const list = extractUsers(res);
      setClients(list);
    } catch (e) {
      toastError("Failed to load clients");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchClients();
  }, [user, filters, searchQuery]);

  // Close action dropdown when clicking outside
  useEffect(() => {
    if (!openActionId) return;
    const handler = (e) => {
      if (!e.target.closest || !document) return;
      const inside = e.target.closest('.client-action-container');
      if (!inside) setOpenActionId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openActionId]);

  /* ================= COLOR MAPS ================= */
  const statusColors = {
    Active: { bg: "#DCF4E9", fg: "#12B76A" },
    "In active": { bg: "#DFE0E1", fg: "#000" },
  };

  const categoryColors = {
    Bronze: { bg: "#F3E1C8", fg: "#000" },
    Silver: { bg: "#E0E0E0", fg: "#000" },
    Gold: { bg: "#FFE082", fg: "#8D6E00" },
    Platinum: { bg: "#CFD8DC", fg: "#000" },
  };

  const accountColors = {
    Pending: { bg: "#FEF1DB", fg: "#F59E0B" },
    Verified: { bg: "#DCF1E4", fg: "#16A34A" },
    Rejected: { bg: "#FADFDF", fg: "#DC2626" },
  };

  const renderBadge = (type, value) => {
    if (!value) return "-";
    let map;
    if (type === "status") map = statusColors[value] || {};
    if (type === "category") map = categoryColors[value] || {};
    if (type === "account") map = accountColors[value] || {};

    const bg = map.bg || "#F0F0F0";
    const fg = map.fg || "#333";

    return (
      <div className="d-flex align-items-center gap-2">
        <span
          style={{
            display: "inline-block",
            padding: "6px 10px",
            borderRadius: 999,
            background: bg,
            color: fg,
            fontWeight: 400,
            fontSize: "0.85rem",
          }}
        >
          {value}
        </span>
      </div>
    );
  };

  // Options for filter dropdowns
  const statusesOptions = Object.keys(statusColors).map((k) => ({ value: k, label: k }));
  const accountStatusesOptions = Object.keys(accountColors).map((k) => ({ value: k, label: k }));

  // Helper to normalize values for display (used for Status/Category labels)
  const getLabel = (v) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "string" || typeof v === "number") return String(v);
    if (typeof v === "object") return v.name || v.label || v._id || "";
    return String(v);
  };
  /* =============== DELETE =============== */
  const handleDelete = async (id) => {
    const res = await confirmDelete("Delete this client?");
    if (!res.isConfirmed) return;

    await axios.delete(`/api/user/${id}`);
    toastSuccess("Client deleted");
    fetchClients();
  };

  /* =============== TABLE DATA =============== */
  const tableData = clients.map((c, i) => ({
    srNo: i + 1,
    Status: (() => {
          const txt = getLabel(c.Status || c.status || '');
          return txt ? (
          <span style={{ background: '#E7F0FE', color: '#3B82F6', padding: '4px 8px', borderRadius: 12, fontSize: '0.85rem' }}>
              {txt }
          </span>
            ) : <span>&nbsp;</span>;
          })(),
    name: `${c.firstName || ""} ${c.lastName || ""}`,
    mobile: c.mobileNumber || "",
    email: c.email || "",
    company: c.business || "",
    gst: c.gstNumber || "",
    status: renderBadge("status", c.userStatus || "-"),
    category: (() => {
      const txt = getLabel(c.userCategory?.name || c.userCategory);
      return txt ? renderBadge("category", txt) : <span>&nbsp;</span>;
    })(),
    accountStatus: renderBadge("account", c.accountStatus || "-"),

    /* 🔐 ACTIONS HIDDEN FOR CLIENT USER */
    action: (() => {
      const cid = c._id;
      const isOwn = user && cid === user._id;

      // Allow clients to View/Edit their own record; other users follow permissions
      const showForClient = isClientUser && isOwn;
      const showForAdmin = !isClientUser && (canViewClient || canEditClient || canDeleteClient);

      if (!showForClient && !showForAdmin) return null;

      return (
     <div className="d-flex align-items-center" style={{ position: "relative" }}>
        <button
          className="btn btn-sm d-flex align-items-center ms-1 shadow-none border-0 bg-transparent"
          title="Actions"
          onClick={(e) => {
            e.stopPropagation();
            setOpenActionId(openActionId === cid ? null : cid);
          }}
          style={{ gap: "4px" }}
        >
          <FaEllipsisV size={14} />
        </button>

        {openActionId === cid && (
          <div
            style={{
              position: "fixed",
              zIndex: 1050,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 6,
              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
              minWidth: 150,
              padding: "6px 0",
            }}
          >
            {(isClientUser ? true : canViewClient) && (
              <button
                className="btn btn-sm w-100 text-start py-1 px-2 lh-sm shadow-none border-0 text-primary-custom d-flex align-items-center"
                onClick={() => {
                  setOpenActionId(null);
                  navigate(`/admin/client/view/${cid}`);
                }}
                style={{ gap: "6px" }}
              >
                <FaEye size={14} /> View
              </button>
            )}

            {(isClientUser ? true : canEditClient) && (
              <button
                className="btn btn-sm w-100 text-start py-1 px-2 lh-sm shadow-none border-0 text-primary-custom d-flex align-items-center"
                onClick={() => {
                  setOpenActionId(null);
                  navigate(`/admin/client/edit/${cid}`);
                }}
                style={{ gap: "6px" }}
              >
                <FaPencilAlt size={14} /> Edit
              </button>
            )}

            {!isClientUser && canDeleteClient && (
              <button
                className="btn btn-sm w-100 text-start py-1 px-2 lh-sm shadow-none border-0 text-danger d-flex align-items-center"
                onClick={() => {
                  setOpenActionId(null);
                  handleDelete(cid);
                }}
                style={{ gap: "6px" }}
              >
                <FaTrashAlt size={14} /> Delete
              </button>
            )}
          </div>
        )}
      </div>
      );
    })()
  }));

  const visibleColumns = columns;

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fw-bold">Client Registration</h4>

        {/* 🔐 HIDE FOR CLIENT */}
        {!isClientUser && (
          <div>
            {/* First Row: Filter + Add */}
            <div className="d-flex justify-content-end gap-2 mb-2">
              <button
                className="btn btn-light btn-sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter /> Filter
              </button>

              {canCreateClient && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate("/admin/client/create")}
                >
                  <FaPlus /> Add Client
                </button>
              )}
            </div>

            <div>
              <input
                className="form-control"
                style={{ width: 260 }}
                placeholder="Search (min 3 chars)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* search moved to header */}

      {showFilters && !isClientUser && (
          <CommanFilters
            filters={{ ...filters, status: filters.userStatus }}
            onFilterChange={(k, v) =>
              setFilters((p) => ({ ...p, [k === 'status' ? 'userStatus' : k]: v }))
            }
            onApplyFilters={fetchClients}
            onClearFilters={handleClearFilters}
            visibleFields={[
              'status',
              'userCategory',
              'accountStatus',
              'dateRange',
              'state',
              'city',
              'country',
            ]}
            dropdowns={{
              statuses: statusesOptions,
              accountStatuses: accountStatusesOptions,
              states: buildOptions(filterDropdowns.states),
              cities: buildOptions(filterDropdowns.cities),
              countries: buildOptions(filterDropdowns.countries),
              userCategories: buildOptions(filterDropdowns.userCategories),
            }}
          />
      )}

      <CommonTable
        columns={visibleColumns}
        data={tableData}
        loading={loading}
      />
    </div>
  );
};

export default ClientIndex;
